import React from 'react';
import { useSpring, animated, config } from 'react-spring';
import SEO from '../../components/SEO';

const PrivacyPolicy = () => {
    // Colors for consistent theming
    const primaryTextColor = "text-[#212529]";
    const secondaryTextColor = "text-[#212529]/80";
    const tertiaryTextColor = "text-[#212529]/60";

    // Animation for content entrance
    const contentAnimation = useSpring({
        from: { opacity: 0, transform: 'translateY(20px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
        config: { ...config.gentle, tension: 120, friction: 14 },
        delay: 100
    });

    return (
        <div className="min-h-screen font-sans overflow-hidden relative">
            <SEO
                title="Privacy Policy - Axialis"
                description="Privacy policy for the Axialis website."
                path="/privacy"
            />
            <div className="container mx-auto px-4 py-32">
                <animated.div style={contentAnimation} className="max-w-4xl mx-auto backdrop-blur-sm bg-white/40 p-8 md:p-12 rounded-xl shadow-xl border border-white/40">
                    <h1 className={`text-4xl font-display font-bold mb-6 ${primaryTextColor}`}>Privacy Policy</h1>
                    <div className={`h-0.5 w-16 bg-primary mb-8`}></div>
                    <p className={`${secondaryTextColor} mb-8`}>Last Updated: {new Date().toLocaleDateString()}</p>

                    <div className="space-y-8">
                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Introduction</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>At Axialis, we believe in keeping things simple and transparent. This privacy policy explains our minimal approach to data handling when you visit our website.</p>
                                <p>We do not track, collect, or store any personal information about our visitors. This website is designed to respect your privacy completely.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>What We Don't Collect</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>To be crystal clear about our privacy practices:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>We do not use analytics or tracking tools</li>
                                    <li>We do not collect any personal information</li>
                                    <li>We do not use tracking cookies</li>
                                    <li>We do not store user data on our servers</li>
                                    <li>We do not share data with third parties (because we don't have any to share)</li>
                                    <li>We do not require consent banners because there's nothing to consent to</li>
                                </ul>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Local Storage</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>The only data stored in relation to your visit is:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><span className={primaryTextColor}>Intro Animation Status:</span> A simple true/false flag stored in your browser's local storage that remembers whether you have already seen the website's intro animation. This contains no personal information and never leaves your device.</li>
                                </ul>
                                <p>This local storage data is completely under your control and can be cleared at any time through your browser settings.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>No Third-Party Services</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>We do not integrate with any third-party tracking services, analytics platforms, or advertising networks. This means:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>No Google Analytics or similar services</li>
                                    <li>No social media tracking pixels</li>
                                    <li>No advertising cookies</li>
                                    <li>No data collection for marketing purposes</li>
                                </ul>
                                <p>Your browsing experience is completely private and not monitored by us or any third parties.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Your Control</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>Since we don't collect any personal data, there's nothing for you to request, delete, or modify. However, if you want to clear the local preferences stored in your browser, you can:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>Clear your browser's local storage</li>
                                    <li>Use your browser's developer tools to manually remove specific items</li>
                                    <li>Use private/incognito browsing mode to prevent any local storage</li>
                                </ul>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Contact Us</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>If you have any questions about this privacy policy, please contact us:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>By email: <a href="mailto:support@rivieratech.solutions" className="text-primary hover:underline">support@rivieratech.solutions</a></li>
                                    <li>By visiting the contact page on our website</li>
                                </ul>
                            </div>
                        </section>

                        {/* Attribution backlink */}
                        <div className="pt-8 mt-8 border-t border-[#212529]/10">
                            <p className="text-[#212529]/40 text-xs text-center">
                                This website was designed and built by{' '}
                                <a
                                    href="https://rivieratech.solutions"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary/60 hover:text-primary hover:underline transition-colors duration-300"
                                >
                                    RivieraTech Solutions
                                </a>
                                {' '}&mdash; a professional website builder specializing in business websites, software development, hardware design, and electronics consulting.
                            </p>
                        </div>
                    </div>
                </animated.div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
